import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, RefreshCw, Building2,
  CheckCircle2, AlertTriangle, XCircle, Globe,
  Shield, Zap, Brain, FileText, FileDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const STATUS_CONFIG = {
  pass: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Pass" },
  warn: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Warning" },
  fail: { icon: XCircle, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200", label: "Fail" },
  found: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Found" },
  inaccurate: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Inaccurate" },
  missing: { icon: XCircle, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200", label: "Missing" },
};

function CircleScore({ score, label, color }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const strokeColor = score >= 70 ? "#0d9488" : score >= 40 ? "#f59e0b" : "#e11d48";

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle cx="60" cy="60" r={radius} fill="none" stroke={strokeColor} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: strokeColor }}>
            {score}
          </span>
          <span className="text-[10px] text-muted-foreground">/100</span>
        </div>
      </div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    </div>
  );
}

function CheckItem({ check }) {
  const config = STATUS_CONFIG[check.status] || STATUS_CONFIG.fail;
  const Icon = config.icon;
  return (
    <div className={`flex items-start gap-2.5 p-2.5 rounded-md border ${config.border} ${config.bg}/30`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${config.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-medium">{check.name}</p>
          <Badge variant="outline" className={`text-[9px] shrink-0 ${config.bg} ${config.color} ${config.border} h-4`}>
            {config.label}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{check.recommendation}</p>
      </div>
    </div>
  );
}

export default function AuditReportPage() {
  const { auditId } = useParams();
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState("");

  const downloadReport = async (format) => {
    setDownloading(format);
    try {
      const suffix = format === "html" ? "?download=true" : "";
      const res = await fetch(`${API}/reports/${auditId}/${format}${suffix}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(company?.name || "report").replace(/[^a-z0-9]+/gi, "_")}_Audit.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch {
      toast.error(`Failed to generate ${format.toUpperCase()}`);
    } finally {
      setDownloading("");
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  const fetchReport = async () => {
    try {
      const res = await fetch(`${API}/reports/${auditId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAudit(data.audit);
        setCompany(data.company);
      }
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const regenerateReport = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/reports/${auditId}/regenerate`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const ai = await res.json();
        setAudit((prev) => ({ ...prev, ai_report: ai }));
        toast.success("Report regenerated");
      }
    } catch { toast.error("Failed to regenerate"); }
    finally { setRegenerating(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!audit) return null;

  const websiteChecks = audit.website_checks || [];
  const presenceChecks = audit.presence_checks || [];
  const wsScore = audit.website_score?.overall || 0;
  const psScore = audit.presence_score?.overall || 0;
  const breakdown = audit.website_score?.breakdown || {};

  const categories = {};
  websiteChecks.forEach((c) => {
    if (!categories[c.category]) categories[c.category] = [];
    categories[c.category].push(c);
  });

  const breakdownData = Object.entries(breakdown).map(([k, v]) => ({ name: k, score: v }));

  return (
    <div data-testid="audit-report-page" className="space-y-4 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Audit Report
            </h1>
            <p className="text-[11px] text-muted-foreground">
              {company?.name} &middot; {audit.domain || "No website"} &middot;{" "}
              {audit.created_at ? new Date(audit.created_at).toLocaleDateString() : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="download-pdf-btn" size="sm" className="h-8 text-xs text-white"
            style={{ backgroundColor: "#4e3c97" }}
            onClick={() => downloadReport("pdf")} disabled={!!downloading}>
            {downloading === "pdf" ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
            Download PDF
          </Button>
          <Button data-testid="download-html-btn" variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => downloadReport("html")} disabled={!!downloading}>
            {downloading === "html" ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
            HTML
          </Button>
          <Button data-testid="regenerate-report-btn" variant="outline" size="sm" className="h-8 text-xs"
            onClick={regenerateReport} disabled={regenerating}>
            {regenerating ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Brain className="w-3.5 h-3.5 mr-1" />}
            Regenerate AI Report
          </Button>
        </div>
      </div>

      {/* Score Overview - 3 circles in a row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex justify-center">
            <CircleScore score={wsScore} label="Website Score" />
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex justify-center">
            <CircleScore score={psScore} label="Presence Score" />
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex justify-center">
            <CircleScore score={audit.priority_score || 0} label="Priority Score" />
          </CardContent>
        </Card>
      </div>

      {/* AI Summary - rendered as markdown */}
      {audit.ai_report?.ai_summary && (
        <Card className="border-indigo-200 shadow-sm bg-indigo-50/30">
          <CardHeader className="pb-1 pt-4 px-5">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="w-4 h-4 text-indigo-600" />
              AI Analysis
              {audit.ai_report.generated && (
                <Badge className="text-[9px] bg-indigo-100 text-indigo-700 border-indigo-200 h-4" variant="outline">
                  GPT-5.2
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="prose prose-sm max-w-none text-slate-700 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:text-[13px] [&_p]:leading-relaxed [&_p]:mb-2 [&_li]:text-[13px] [&_li]:leading-relaxed [&_strong]:text-slate-900 [&_ul]:my-1 [&_ol]:my-1">
              <ReactMarkdown>{audit.ai_report.ai_summary}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="website" className="space-y-3">
        <TabsList className="h-9">
          <TabsTrigger data-testid="tab-website" value="website" className="text-xs h-7">
            <Globe className="w-3.5 h-3.5 mr-1" /> Website ({websiteChecks.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-presence" value="presence" className="text-xs h-7">
            <Shield className="w-3.5 h-3.5 mr-1" /> Presence ({presenceChecks.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-breakdown" value="breakdown" className="text-xs h-7">
            <Zap className="w-3.5 h-3.5 mr-1" /> Breakdown
          </TabsTrigger>
        </TabsList>

        <TabsContent value="website">
          {Object.entries(categories).map(([cat, checks]) => {
            const passed = checks.filter((c) => c.status === "pass").length;
            return (
              <div key={cat} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{cat}</h3>
                  <span className="text-[10px] text-muted-foreground">{passed}/{checks.length} passed</span>
                </div>
                <div className="space-y-1.5">
                  {checks.map((check, i) => <CheckItem key={i} check={check} />)}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="presence">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {presenceChecks.map((check, i) => {
              const config = STATUS_CONFIG[check.status] || STATUS_CONFIG.missing;
              const Icon = config.icon;
              return (
                <div key={i} className={`flex items-center gap-2.5 p-3 rounded-md border ${config.border} ${config.bg}/30`}>
                  <Icon className={`w-4 h-4 shrink-0 ${config.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] font-medium">{check.directory}</p>
                      <Badge variant="outline" className={`text-[9px] ${config.bg} ${config.color} ${config.border} h-4`}>
                        {config.label}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{check.recommendation}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex gap-4">
            <div className="flex items-center gap-1 text-[11px]">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Found: {audit.presence_score?.found || 0}
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              <AlertTriangle className="w-3 h-3 text-amber-600" /> Inaccurate: {audit.presence_score?.inaccurate || 0}
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              <XCircle className="w-3 h-3 text-rose-600" /> Missing: {audit.presence_score?.missing || 0}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="breakdown">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={breakdownData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="score" name="Score" radius={[0, 4, 4, 0]} barSize={18}>
                      {breakdownData.map((entry, i) => (
                        <Cell key={i} fill={entry.score >= 70 ? "#0d9488" : entry.score >= 40 ? "#f59e0b" : "#e11d48"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
